import { FormGroupState, KeyValue } from 'ngrx-forms';

import { ColumnState, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { InjectionToken } from '@angular/core';
import { Params } from '@angular/router';
import {
  AgGridFilterModel,
  FilterFormValueInterface,
  GridState,
} from '@ideapa/app-common';
import { FieldConfig, FilterValue } from '@ideapa/common-interfaces';
import { Action, ActionCreator } from '@ngrx/store';

export const LAZY_ENTITY_ACTIONS_TOKEN = new InjectionToken<string>(
  'LAZY_ENTITY_ACTIONS'
);

export const LAZY_ENTITY_SELECTORS_TOKEN = new InjectionToken<string>(
  'LAZY_ENTITY_SELECTORS'
);

export const ENTITY_FORM_ID_TOKEN = new InjectionToken<string>(
  'ENTITY_FORM_ID'
);

export const ENTITY_FILTER_FORM_ID_TOKEN = new InjectionToken<string>(
  'ENTITY_FILTER_FORM_ID'
);

export interface LazyEntityState<Entity, EntityFormValue extends KeyValue> {
  entityForm: FormGroupState<EntityFormValue>;
  entityFilterForm: FormGroupState<FilterFormValueInterface>;
  selectedId: number | undefined;
  userByEmail?: Entity;
  gridState: GridState;
  loadedEntity: Entity | undefined;
  showAdvancedFilters: boolean;
}

// export interface BaseLazyEntityFacadeInterface<T, K extends KeyValue> {
//   getPageSize$: Observable<number>;
//   getCurrentPage$: Observable<number>;
//   getTotalRows$: Observable<number>;
//   getColumnState$: Observable<ColumnState[] | undefined>;
//   getFilterModel$: Observable<AgGridFilterModel | undefined>;
//   pageLoaded$: Observable<boolean>;
//   getLoadedEntity$: Observable<T | undefined>;
//   filterForm$: Observable<FormGroupState<FilterFormValueInterface>>;
//   entity$: Observable<T | undefined>;
//   entityId$: Observable<number | undefined>;
//   form$: Observable<FormGroupState<K>>;
//   getBreadcrumb$: Observable<any[]>;
//   showAdvancedFilters$: Observable<boolean>;
//   queryParams$: Observable<Params>;

//   setPageSize: (pageSize: number) => void;
//   setCurrentPage: (currentPage: number) => void;
//   formInitialize: (
//     item: T | undefined,
//     disable?: boolean,
//     options?: any,
//     permissions?: string[]
//   ) => void;
//   setPageLoaded: (loaded: boolean) => void;
//   setTotalRows: (totalRows: number) => void;
//   formSave: (form: FormGroupState<K>, callback?: (item: T) => void) => void;
//   formDelete: () => void;
//   resetForm: () => void;
//   setFilterModel: (filterModel: AgGridFilterModel) => void;
//   setColumnState: (sortModel: ColumnState[]) => void;
//   setFilterFormValue: (value: FilterValue[]) => void;
//   displayFormError: () => void;
//   downloadExcel: (
//     fileName?: string,
//     columns?: FieldConfig[],
//     join?: string[]
//   ) => void;
//   selectEntityId: (id: number) => void;
//   clearEntityId: () => void;
//   addEntity: () => void;
//   showAdvancedFilters: (show: boolean) => void;
// }

export interface LazyEntitySelectors<T, V, K extends KeyValue> {
  selectEntityForm: (state: any) => FormGroupState<K>;
  selectEntityId: (state: V) => number | undefined;
  isNew: (state: V) => boolean;
  getSelectedEntity: (state: V) => T | undefined;
  selectEntityFilterForm: (
    state: V
  ) => FormGroupState<FilterFormValueInterface>;

  getGridState: (state: V) => GridState;
  getPageSize: (state: V) => number;
  getCurrentPage: (state: V) => number;
  getTotalRows: (state: V) => number;
  pageLoaded: (state: V) => boolean;
  getColumnState: (state: V) => ColumnState[] | undefined;
  getFilterModel: (state: V) => AgGridFilterModel | undefined;
  getLoadedEntity: (state: V) => T | undefined;
  showAdvancedFilters: (state: V) => boolean;
  getQueryParams: (state: V) => Params;
}

export interface LazyEntityActions<T, K extends KeyValue> {
  formDelete: ActionCreator<string, () => Action>;
  formSave: ActionCreator<
    string,
    (props: {
      form: FormGroupState<K>;
      callback?: ((item: T) => void) | undefined;
    }) => {
      form: FormGroupState<K>;
      callback?: ((item: T) => void) | undefined;
    } & Action
  >;
  setFilterModel: ActionCreator<
    string,
    (props: { filterModel?: AgGridFilterModel }) => {
      filterModel?: AgGridFilterModel;
    } & Action
  >;
  loadGridPage: ActionCreator<
    string,
    (props: { params: IServerSideGetRowsParams }) => {
      params: IServerSideGetRowsParams;
    } & Action
  >;
  loadGridPageSuccess: ActionCreator<
    string,
    (props: { rows: T[] }) => {
      rows: T[];
    } & Action
  >;
  displayFormError: ActionCreator<string, () => Action>;
  setEntity: ActionCreator<
    string,
    (props: { entity?: T }) => {
      entity?: T;
    } & Action
  >;
  setGridState: ActionCreator<
    string,
    (props: { gridState: GridState }) => {
      gridState: GridState;
    } & Action
  >;
  setPageLoaded: ActionCreator<
    string,
    (props: { loaded: boolean }) => {
      loaded: boolean;
    } & Action
  >;
  setPageSize: ActionCreator<
    string,
    (props: { pageSize: number }) => {
      pageSize: number;
    } & Action
  >;
  setTotalRows: ActionCreator<
    string,
    (props: { totalRows: number }) => {
      totalRows: number;
    } & Action
  >;
  showAdvancedFilters: ActionCreator<
    string,
    (props: { show: boolean }) => {
      show: boolean;
    } & Action
  >;
  downloadExcel: ActionCreator<
    string,
    (props: {
      fileName?: string;
      columns?: FieldConfig[];
      join?: string[];
      filters?: FilterValue[];
      orderBy?: { [field: string]: 'asc' | 'desc' };
    }) => {
      fileName?: string;
      columns?: FieldConfig[];
      join?: string[];
      filters?: FilterValue[];
      orderBy?: { [field: string]: 'asc' | 'desc' };
    } & Action
  >;
  selectEntityId: ActionCreator<
    string,
    (props: { id: number | undefined }) => {
      id: number | undefined;
    } & Action
  >;
  addEntity: ActionCreator<string, () => Action>;
  selectItem: ActionCreator<
    string,
    (props: { item: T }) => {
      item: T;
    } & Action
  >;
  setColumnState: ActionCreator<
    string,
    (props: { columnState?: ColumnState[] }) => {
      columnState?: ColumnState[];
    } & Action
  >;
  setCurrentPage: ActionCreator<
    string,
    (props: { currentPage: number }) => {
      currentPage: number;
    } & Action
  >;

  manualClose: ActionCreator<
    string,
    (props: {
      path: string;
      navigate?: boolean;
    }) => { path: string; navigate?: boolean } & Action
  >;

  addSuccess: ActionCreator<
    string,
    (props: { item: T; notify: boolean; goBack: boolean }) => {
      item: T;
      notify: boolean;
      goBack: boolean;
    } & Action
  >;
  updateSuccess: ActionCreator<
    string,
    (props: {
      item: T;
      notify: boolean;
      goBack: boolean;
      callback?: (item: T) => void;
    }) => {
      item: T;
      notify: boolean;
      goBack: boolean;
      callback?: (item: T) => void;
    } & Action
  >;
  loadSingleSuccess: ActionCreator<
    string,
    (props: { item: T }) => {
      item: T;
    } & Action
  >;
  resetState: ActionCreator<
    string,
    (props: { entity: string }) => {
      entity: string;
    } & Action
  >;
}

// export interface EntityAdapter<T> extends EntityStateAdapter<T> {
//   selectId: IdSelector<T>;
//   sortComparer: false | Comparer<T>;
//   getInitialState(): LazyEntityState<T>;
//   getInitialState<S extends object>(state: S): LazyEntityState<T> & S;
//   getSelectors(): EntitySelectors<T, LazyEntityState<T>>;
//   getSelectors<V>(
//     selectState: (state: V) => LazyEntityState<T>
//   ): EntitySelectors<T, V>;
// }
